import React, { useState, useEffect } from 'react';
import { ChefHat, MapPin, Phone, Mail, Clock, Award, Users, Menu, X, Globe } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useLanguage } from '@/contexts/LanguageContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { menuData } from '@/data/menuData';

const Home = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const { language, changeLanguage, t } = useLanguage();

  const videos = [
    'https://customer-assets.emergentagent.com/job_1d48e4ae-8fda-4316-a0ae-bce8eda22ce4/artifacts/zpnmo2b6_0%20Italian%20Wooden%20Table%201280X720%20small.mp4',
    'https://customer-assets.emergentagent.com/job_elit-grill/artifacts/8kugvhiy_0%20Burrito%20Chicken%201280X720%20small.mp4',
    'https://customer-assets.emergentagent.com/job_elit-grill/artifacts/3nzlilib_0%20Steak%20Grilled%201280X720%20small.mp4',
    'https://customer-assets.emergentagent.com/job_elit-grill/artifacts/t7zyb4v4_0%20Chicken%20Breaded%20Chicken%201280X720%20small.mp4'
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const videoInterval = setInterval(() => {
      setCurrentVideoIndex((prevIndex) => (prevIndex + 1) % videos.length);
    }, 8000); // Change video every 8 seconds
    
    return () => clearInterval(videoInterval);
  }, [videos.length]);

  const openingHours = [
    { day: t.contact.days.monThu, hours: '10:30-22:00' },
    { day: t.contact.days.fri, hours: '10:30-23:00' },
    { day: t.contact.days.sat, hours: '11:00-23:00' },
    { day: t.contact.days.sun, hours: '11:00-22:00' }
  ];

  return (
    <div className="min-h-screen bg-black">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-black/95 backdrop-blur-md shadow-lg' : 'bg-transparent'
      }`}>
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_1d48e4ae-8fda-4316-a0ae-bce8eda22ce4/artifacts/g4gudmr7_ravintola%20elit%20logo.svg" 
              alt="Ravintola Elit Logo" 
              className="h-12 w-auto"
            />
          </div>
          <div className="hidden md:flex items-center space-x-8">
            <a href="#about" className="text-white hover:text-red-600 transition-colors">{t.nav.story}</a>
            <a href="#menu" className="text-white hover:text-red-600 transition-colors">{t.nav.menu}</a>
            <a href="#contact" className="text-white hover:text-red-600 transition-colors">{t.nav.contact}</a>
            
            {/* Language Switcher */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="bg-green-900/30 border-green-700/50 text-white hover:bg-green-900/50">
                  <Globe className="w-4 h-4 mr-2" />
                  {language.toUpperCase()}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-black/95 border-green-900/30">
                <DropdownMenuItem 
                  onClick={() => changeLanguage('fi')}
                  className="text-white hover:bg-green-900/30 cursor-pointer"
                >
                  Suomi (FI)
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={() => changeLanguage('en')}
                  className="text-white hover:bg-green-900/30 cursor-pointer"
                >
                  English (EN)
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={() => changeLanguage('sv')}
                  className="text-white hover:bg-green-900/30 cursor-pointer"
                >
                  Svenska (SV)
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          <button 
            className="md:hidden text-white p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
        
        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-black/95 backdrop-blur-md border-t border-green-900/30">
            <div className="container mx-auto px-4 py-4 flex flex-col space-y-4">
              <a 
                href="#about" 
                className="text-white hover:text-red-600 transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                {t.nav.story}
              </a>
              <a 
                href="#menu" 
                className="text-white hover:text-red-600 transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                {t.nav.menu}
              </a>
              <a 
                href="#contact" 
                className="text-white hover:text-red-600 transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                {t.nav.contact}
              </a>
              
              {/* Mobile Language Switcher */}
              <div className="flex space-x-2 pt-2 border-t border-green-900/30">
                <Button 
                  variant={language === 'fi' ? 'default' : 'outline'} 
                  size="sm" 
                  onClick={() => changeLanguage('fi')}
                  className={language === 'fi' ? 'bg-green-700' : 'bg-green-900/30 border-green-700/50 text-white'}
                >
                  FI
                </Button>
                <Button 
                  variant={language === 'en' ? 'default' : 'outline'} 
                  size="sm" 
                  onClick={() => changeLanguage('en')}
                  className={language === 'en' ? 'bg-green-700' : 'bg-green-900/30 border-green-700/50 text-white'}
                >
                  EN
                </Button>
                <Button 
                  variant={language === 'sv' ? 'default' : 'outline'} 
                  size="sm" 
                  onClick={() => changeLanguage('sv')}
                  className={language === 'sv' ? 'bg-green-700' : 'bg-green-900/30 border-green-700/50 text-white'}
                >
                  SV
                </Button>
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section with Video Slider */}
      <section className="relative h-screen overflow-hidden">
        {/* Darker overlay - 40% opacity */}
        <div className="absolute inset-0 bg-black/60 z-10"></div>
        
        {/* Video Slider */}
        {videos.map((video, index) => (
          <video 
            key={index}
            autoPlay 
            muted 
            loop 
            playsInline
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${
              index === currentVideoIndex ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <source src={video} type="video/mp4" />
          </video>
        ))}
        
        <div className="relative z-20 h-full flex flex-col items-center justify-center text-center px-4">
          <div className="mb-6 inline-block px-4 py-2 bg-green-900/30 backdrop-blur-sm rounded-full border border-green-700/50">
            <span className="text-green-400 font-semibold">{t.hero.est}</span>
          </div>
          <h1 className="text-6xl md:text-8xl font-bold text-white mb-6 tracking-tight">
            RAVINTOLA<br />
            <span className="text-red-600">ELIT</span>
          </h1>
          <p className="text-xl md:text-2xl text-gray-200 max-w-3xl mb-12 leading-relaxed">
            {t.hero.tagline}
          </p>
          
          {/* Order Food Button */}
          <Button 
            size="lg"
            className="bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-bold px-12 py-6 text-lg rounded-full shadow-2xl transform hover:scale-105 transition-all duration-300 mb-12"
            onClick={() => window.open('https://www.restaurantlogin.com/api/fb/_z9g_y_y', '_blank')}
          >
            {t.hero.orderButton}
          </Button>
          
          {/* Video indicators */}
          <div className="flex space-x-2">
            {videos.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentVideoIndex(index)}
                className={`w-2 h-2 rounded-full transition-all ${
                  index === currentVideoIndex ? 'bg-red-600 w-8' : 'bg-white/50'
                }`}
                aria-label={`View video ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-24 px-4 bg-gradient-to-b from-black to-green-950/20">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <div className="inline-block mb-4">
              <span className="text-green-500 font-semibold text-sm tracking-wider uppercase">{t.about.since}</span>
            </div>
            <h2 className="text-5xl md:text-6xl font-bold text-white mb-6">{t.about.title}</h2>
          </div>
          
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <p className="text-gray-300 text-lg leading-relaxed">
                {t.about.text1}
              </p>
              <p className="text-gray-300 text-lg leading-relaxed">
                {t.about.text2}
              </p>
              <div className="grid grid-cols-2 gap-6 pt-8">
                <Card className="bg-green-900/20 border-green-700/30 p-6 text-center backdrop-blur-sm hover:bg-green-900/30 transition-all">
                  <div className="text-4xl font-bold text-green-500 mb-2">50+</div>
                  <div className="text-gray-400 text-sm">{t.about.dishes}</div>
                </Card>
                <Card className="bg-red-900/20 border-red-700/30 p-6 text-center backdrop-blur-sm hover:bg-red-900/30 transition-all">
                  <div className="text-4xl font-bold text-red-500 mb-2">100%</div>
                  <div className="text-gray-400 text-sm">{t.about.quality}</div>
                </Card>
              </div>
            </div>
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-green-600/20 to-red-600/20 rounded-lg blur-3xl"></div>
              <img 
                src="https://customer-assets.emergentagent.com/job_elit-grill/artifacts/d7gwfgdm_freepik_make-this-image-looks-better-but-it-must-be-real-picture-like-this.-only-change-saturation-make-it-looks-nice-for-website-and-presentation_0001.jpg" 
                alt="Ravintola Elit"
                className="relative rounded-lg shadow-2xl w-full h-auto"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Menu Section */}
      <section id="menu" className="py-24 px-4 bg-gradient-to-b from-green-950/20 to-black">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center mb-16">
            <div className="inline-block mb-4 px-4 py-2 bg-red-900/20 rounded-full border border-red-700/30">
              <span className="text-red-400 font-semibold text-sm">{t.menu.subtitle}</span>
            </div>
            <h2 className="text-5xl md:text-6xl font-bold text-white mb-4">{t.menu.title}</h2>
            <p className="text-gray-400 text-sm mt-4">Norm / Perhe | +4,00€ Gluteeniton pizzapohja</p>
          </div>

          <Tabs defaultValue="pizzas-classic" className="w-full">
            <TabsList className="grid w-full grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2 bg-transparent mb-12 h-auto">
              <TabsTrigger 
                value="pizzas-classic" 
                className="data-[state=active]:bg-red-900/30 data-[state=active]:text-red-400 border border-red-900/30 text-gray-400 whitespace-nowrap"
              >
                🍕 Pizzat
              </TabsTrigger>
              <TabsTrigger 
                value="salads" 
                className="data-[state=active]:bg-red-900/30 data-[state=active]:text-red-400 border border-red-900/30 text-gray-400"
              >
                🥗 Salaatit
              </TabsTrigger>
              <TabsTrigger 
                value="burgers" 
                className="data-[state=active]:bg-red-900/30 data-[state=active]:text-red-400 border border-red-900/30 text-gray-400"
              >
                🍔 Burgerit
              </TabsTrigger>
              <TabsTrigger 
                value="kebabs" 
                className="data-[state=active]:bg-red-900/30 data-[state=active]:text-red-400 border border-red-900/30 text-gray-400"
              >
                🥙 Kebabit
              </TabsTrigger>
              <TabsTrigger 
                value="chicken" 
                className="data-[state=active]:bg-red-900/30 data-[state=active]:text-red-400 border border-red-900/30 text-gray-400"
              >
                🍗 Kanaruoat
              </TabsTrigger>
              <TabsTrigger 
                value="steaks" 
                className="data-[state=active]:bg-red-900/30 data-[state=active]:text-red-400 border border-red-900/30 text-gray-400"
              >
                🥩 Pihvit
              </TabsTrigger>
              <TabsTrigger 
                value="more" 
                className="data-[state=active]:bg-red-900/30 data-[state=active]:text-red-400 border border-red-900/30 text-gray-400"
              >
                ➕ Lisää
              </TabsTrigger>
            </TabsList>

            {/* Pizzas Classic */}
            <TabsContent value="pizzas-classic" className="space-y-8">
              {/* Classic Pizzas */}
              <div>
                <h3 className="text-3xl font-bold text-white mb-6">Klassikot</h3>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {menuData.fi.categories[0].items.map((item, idx) => (
                    <Card key={idx} className="bg-gradient-to-br from-green-950/30 to-black border-green-900/30 p-4 hover:border-green-700/50 transition-all group">
                      <div className="flex justify-between items-start mb-1">
                        <h4 className="text-lg font-bold text-white group-hover:text-green-400 transition-colors">{item.name}</h4>
                        <span className="text-red-500 font-bold text-sm whitespace-nowrap ml-2">{item.price}</span>
                      </div>
                      {item.desc && <p className="text-gray-400 text-xs">{item.desc}</p>}
                    </Card>
                  ))}
                </div>
              </div>

              {/* Premium Pizzas */}
              <div>
                <h3 className="text-3xl font-bold text-white mb-6">Premium Pizzat</h3>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {menuData.fi.categories[1].items.map((item, idx) => (
                    <Card key={idx} className="bg-gradient-to-br from-red-950/30 to-black border-red-900/30 p-4 hover:border-red-700/50 transition-all group">
                      <div className="flex justify-between items-start mb-1">
                        <h4 className="text-lg font-bold text-white group-hover:text-red-400 transition-colors">{item.name}</h4>
                        <span className="text-red-500 font-bold text-sm whitespace-nowrap ml-2">{item.price}</span>
                      </div>
                      {item.desc && <p className="text-gray-400 text-xs">{item.desc}</p>}
                    </Card>
                  ))}
                </div>
              </div>

              {/* Own Choice */}
              <div>
                <h3 className="text-3xl font-bold text-white mb-6">Oma valinta</h3>
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {menuData.fi.categories[2].items.map((item, idx) => (
                    <Card key={idx} className="bg-gradient-to-br from-green-950/30 to-black border-green-900/30 p-4 hover:border-green-700/50 transition-all">
                      <div className="text-center">
                        <h4 className="text-lg font-bold text-white mb-1">{item.name}</h4>
                        <span className="text-red-500 font-bold">{item.price}</span>
                      </div>
                    </Card>
                  ))}
                </div>
                <p className="text-gray-400 text-sm mt-4">Lisätäytteet: Norm. 1,50€ / Perhe 3,00€</p>
              </div>
            </TabsContent>

            {/* Salads */}
            <TabsContent value="salads">
              <div className="grid md:grid-cols-2 gap-6">
                {menuData.fi.categories[3].items.map((item, idx) => (
                  <Card key={idx} className="bg-gradient-to-br from-green-950/30 to-black border-green-900/30 p-6 hover:border-green-700/50 transition-all group">
                    <div className="flex justify-between items-start mb-2">
                      <h3 className="text-xl font-bold text-white group-hover:text-green-400 transition-colors">{item.name}</h3>
                      <span className="text-red-500 font-bold">{item.price}</span>
                    </div>
                    <p className="text-gray-400 text-sm">{item.desc}</p>
                  </Card>
                ))}
              </div>
            </TabsContent>

            {/* Burgers */}
            <TabsContent value="burgers">
              <h3 className="text-2xl font-bold text-white mb-4">Burgerit (Norm / Ateria)</h3>
              <p className="text-gray-400 text-sm mb-6">Ateria sisältää ranskalaiset + 0,33 limu</p>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {menuData.fi.categories[11].items.map((item, idx) => (
                  <Card key={idx} className="bg-gradient-to-br from-green-950/30 to-black border-green-900/30 p-4 hover:border-green-700/50 transition-all group">
                    <div className="flex justify-between items-start mb-1">
                      <h4 className="text-lg font-bold text-white group-hover:text-green-400 transition-colors">{item.name}</h4>
                      <span className="text-red-500 font-bold text-sm whitespace-nowrap ml-2">{item.price}</span>
                    </div>
                    {item.desc && <p className="text-gray-400 text-xs">{item.desc}</p>}
                  </Card>
                ))}
              </div>
            </TabsContent>

            {/* Kebabs */}
            <TabsContent value="kebabs">
              <h3 className="text-2xl font-bold text-white mb-4">Kanakebabit</h3>
              <p className="text-gray-400 text-sm mb-6">Tuplaliha +6,00€</p>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                {menuData.fi.categories[12].items.map((item, idx) => (
                  <Card key={idx} className="bg-gradient-to-br from-red-950/30 to-black border-red-900/30 p-4 hover:border-red-700/50 transition-all">
                    <div className="text-center">
                      <h4 className="text-lg font-bold text-white mb-1">{item.name}</h4>
                      <span className="text-red-500 font-bold">{item.price}</span>
                    </div>
                  </Card>
                ))}
              </div>
            </TabsContent>

            {/* Chicken Dishes */}
            <TabsContent value="chicken">
              <div className="grid md:grid-cols-2 gap-6">
                {menuData.fi.categories[8].items.map((item, idx) => (
                  <Card key={idx} className="bg-gradient-to-br from-green-950/30 to-black border-green-900/30 p-6 hover:border-green-700/50 transition-all group">
                    <div className="flex justify-between items-start mb-2">
                      <h3 className="text-xl font-bold text-white group-hover:text-green-400 transition-colors">{item.name}</h3>
                      <span className="text-red-500 font-bold">{item.price}</span>
                    </div>
                    <p className="text-gray-400 text-sm">{item.desc}</p>
                  </Card>
                ))}
              </div>
            </TabsContent>

            {/* Steaks */}
            <TabsContent value="steaks">
              <div className="grid md:grid-cols-2 gap-6">
                {menuData.fi.categories[10].items.map((item, idx) => (
                  <Card key={idx} className="bg-gradient-to-br from-red-950/30 to-black border-red-900/30 p-6 hover:border-red-700/50 transition-all group">
                    <div className="flex justify-between items-start mb-2">
                      <h3 className="text-xl font-bold text-white group-hover:text-red-400 transition-colors">{item.name}</h3>
                      <span className="text-red-500 font-bold">{item.price}</span>
                    </div>
                    <p className="text-gray-400 text-sm">{item.desc}</p>
                  </Card>
                ))}
              </div>
            </TabsContent>

            {/* More */}
            <TabsContent value="more" className="space-y-12">
              {/* Falafel */}
              <div>
                <h3 className="text-3xl font-bold text-white mb-6">🧆 Falafelit</h3>
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {menuData.fi.categories[4].items.map((item, idx) => (
                    <Card key={idx} className="bg-gradient-to-br from-green-950/30 to-black border-green-900/30 p-4 hover:border-green-700/50 transition-all">
                      <div className="text-center">
                        <h4 className="text-lg font-bold text-white mb-1">{item.name}</h4>
                        <span className="text-red-500 font-bold">{item.price}</span>
                        {item.desc && <p className="text-gray-400 text-xs mt-1">{item.desc}</p>}
                      </div>
                    </Card>
                  ))}
                </div>
              </div>

              {/* Snacks */}
              <div>
                <h3 className="text-3xl font-bold text-white mb-6">🍟 Snäks Ateriat</h3>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {menuData.fi.categories[5].items.map((item, idx) => (
                    <Card key={idx} className="bg-gradient-to-br from-red-950/30 to-black border-red-900/30 p-4 hover:border-red-700/50 transition-all">
                      <div className="flex justify-between items-start mb-1">
                        <h4 className="text-lg font-bold text-white">{item.name}</h4>
                        <span className="text-red-500 font-bold text-sm whitespace-nowrap ml-2">{item.price}</span>
                      </div>
                      {item.desc && <p className="text-gray-400 text-xs">{item.desc}</p>}
                    </Card>
                  ))}
                </div>
              </div>

              {/* Kids Menu */}
              <div>
                <h3 className="text-3xl font-bold text-white mb-6">👶 Lapsille</h3>
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {menuData.fi.categories[6].items.map((item, idx) => (
                    <Card key={idx} className="bg-gradient-to-br from-green-950/30 to-black border-green-900/30 p-4 hover:border-green-700/50 transition-all">
                      <div className="text-center">
                        <h4 className="text-lg font-bold text-white mb-1">{item.name}</h4>
                        <span className="text-red-500 font-bold">{item.price}</span>
                        {item.desc && <p className="text-gray-400 text-xs mt-1">{item.desc}</p>}
                      </div>
                    </Card>
                  ))}
                </div>
              </div>

              {/* Burritos */}
              <div>
                <h3 className="text-3xl font-bold text-white mb-6">🌯 Burritot</h3>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {menuData.fi.categories[7].items.map((item, idx) => (
                    <Card key={idx} className="bg-gradient-to-br from-red-950/30 to-black border-red-900/30 p-4 hover:border-red-700/50 transition-all">
                      <div className="flex justify-between items-start mb-1">
                        <h4 className="text-lg font-bold text-white">{item.name}</h4>
                        <span className="text-red-500 font-bold text-sm whitespace-nowrap ml-2">{item.price}</span>
                      </div>
                      <p className="text-gray-400 text-xs">{item.desc}</p>
                    </Card>
                  ))}
                </div>
              </div>

              {/* Schnitzels */}
              <div>
                <h3 className="text-3xl font-bold text-white mb-6">🍖 Leikkeet</h3>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {menuData.fi.categories[9].items.map((item, idx) => (
                    <Card key={idx} className="bg-gradient-to-br from-green-950/30 to-black border-green-900/30 p-4 hover:border-green-700/50 transition-all">
                      <div className="flex justify-between items-start mb-1">
                        <h4 className="text-lg font-bold text-white">{item.name}</h4>
                        <span className="text-red-500 font-bold text-sm whitespace-nowrap ml-2">{item.price}</span>
                      </div>
                      <p className="text-gray-400 text-xs">{item.desc}</p>
                    </Card>
                  ))}
                </div>
              </div>

              {/* Hot Wings */}
              <div>
                <h3 className="text-3xl font-bold text-white mb-6">🍗 Hot Wings</h3>
                <div className="grid md:grid-cols-3 gap-4 mb-4">
                  {menuData.fi.categories[13].items.map((item, idx) => (
                    <Card key={idx} className="bg-gradient-to-br from-red-950/30 to-black border-red-900/30 p-4 hover:border-red-700/50 transition-all">
                      <div className="text-center">
                        <h4 className="text-lg font-bold text-white mb-1">{item.name}</h4>
                        <span className="text-red-500 font-bold">{item.price}</span>
                      </div>
                    </Card>
                  ))}
                </div>
                <p className="text-gray-400 text-sm">Lisukkeet: Ranskalaiset / lohkoperunat 4,00€ | Ristikkoperunat / juustoperunat 6,00€</p>
              </div>

              {/* Drinks */}
              <div>
                <h3 className="text-3xl font-bold text-white mb-6">🥤 Juomat</h3>
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {menuData.fi.categories[14].items.map((item, idx) => (
                    <Card key={idx} className="bg-gradient-to-br from-green-950/30 to-black border-green-900/30 p-4 hover:border-green-700/50 transition-all">
                      <div className="flex justify-between items-center">
                        <h4 className="text-lg font-bold text-white">{item.name}</h4>
                        <span className="text-red-500 font-bold">{item.price}</span>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 px-4 bg-gradient-to-b from-black to-green-950/20">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-5xl md:text-6xl font-bold text-white text-center mb-16">{t.contact.title}</h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
            <Card className="bg-gradient-to-br from-green-950/30 to-black border-green-900/30 p-8 hover:border-green-700/50 transition-all">
              <MapPin className="w-10 h-10 text-green-500 mb-4" />
              <h3 className="text-white font-bold mb-2 text-lg">{t.contact.address}</h3>
              <p className="text-gray-400">Kirkkotie 175<br />07170 Pornainen</p>
            </Card>

            <Card className="bg-gradient-to-br from-red-950/30 to-black border-red-900/30 p-8 hover:border-red-700/50 transition-all">
              <Phone className="w-10 h-10 text-red-500 mb-4" />
              <h3 className="text-white font-bold mb-2 text-lg">{t.contact.phone}</h3>
              <a href="tel:0413199626" className="text-gray-400 hover:text-red-400 transition-colors">
                041 319 9626
              </a>
            </Card>

            <Card className="bg-gradient-to-br from-green-950/30 to-black border-green-900/30 p-8 hover:border-green-700/50 transition-all">
              <Mail className="w-10 h-10 text-green-500 mb-4" />
              <h3 className="text-white font-bold mb-2 text-lg">{t.contact.email}</h3>
              <a href="mailto:info@ravintolaelit.fi" className="text-gray-400 hover:text-green-400 transition-colors break-all">
                info@ravintolaelit.fi
              </a>
            </Card>

            <Card className="bg-gradient-to-br from-red-950/30 to-black border-red-900/30 p-8 hover:border-red-700/50 transition-all">
              <Clock className="w-10 h-10 text-red-500 mb-4" />
              <h3 className="text-white font-bold mb-2 text-lg">{t.contact.hours}</h3>
              <div className="space-y-1">
                {openingHours.map((hour, idx) => (
                  <div key={idx} className="flex justify-between text-sm">
                    <span className="text-gray-400">{hour.day}:</span>
                    <span className="text-gray-300">{hour.hours}</span>
                  </div>
                ))}
              </div>
            </Card>
          </div>

          {/* Google Map */}
          <div className="mt-12">
            <h3 className="text-3xl font-bold text-white text-center mb-8">{t.contact.location}</h3>
            <div className="relative w-full h-96 rounded-lg overflow-hidden shadow-2xl border border-green-900/30">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1942.2576881234567!2d25.3767!3d60.4749!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x468df0e8e8e8e8e8%3A0x0!2sKirkkotie%20175%2C%2007170%20Pornainen!5e0!3m2!1sfi!2sfi!4v1234567890"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Ravintola Elit Location"
              ></iframe>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black border-t border-green-900/30 py-12 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <img 
                src="https://customer-assets.emergentagent.com/job_1d48e4ae-8fda-4316-a0ae-bce8eda22ce4/artifacts/g4gudmr7_ravintola%20elit%20logo.svg" 
                alt="Ravintola Elit Logo" 
                className="h-10 w-auto mb-4"
              />
              <p className="text-gray-400 text-sm">{t.footer.tagline}</p>
            </div>
            <div>
              <h4 className="text-white font-bold mb-4">{t.footer.contactInfo}</h4>
              <div className="space-y-2 text-sm text-gray-400">
                <p>Kirkkotie 175, 07170 Pornainen</p>
                <p>041 319 9626</p>
                <p>info@ravintolaelit.fi</p>
              </div>
            </div>
            <div>
              <h4 className="text-white font-bold mb-4">{t.footer.companyInfo}</h4>
              <div className="space-y-2 text-sm text-gray-400">
                <p>Nupel Oy</p>
                <p>Y-tunnus: 3190122-3</p>
              </div>
            </div>
          </div>
          <div className="border-t border-green-900/30 pt-8 text-center">
            <p className="text-gray-500 text-sm">
              © {new Date().getFullYear()} Ravintola Elit. {t.footer.rights}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;